// Columns each table can show, for the column picker (the gear icon on the
// client page tables). Pure, no React. The order here is the order on screen;
// users only choose which ones are visible.
//
//   key       stored in the user's saved view (keep it stable)
//   label     header + name in the picker
//   on        shown by default
//   locked    always shown, cannot be unticked
//   align     'right' for amounts
//   needs     'multiCurrency' = only offered on USD + MXN properties
//
// Keep the table keys in sync with TABLE_KEYS in backend/table_prefs.py.

export const TABLES = {
  client_bookings: {
    label: 'Bookings',
    columns: [
      { key: 'run_date', label: 'Runs', on: true },
      { key: 'created', label: 'Created', on: false },
      { key: 'where', label: 'Edition / schedule', on: true },
      { key: 'category', label: 'Type', on: false },
      { key: 'product', label: 'Product', on: true },
      { key: 'size', label: 'Size', on: false },
      { key: 'page', label: 'Page / position', on: false },
      { key: 'rep', label: 'Rep', on: true },
      { key: 'list_rate', label: 'List rate', on: false, align: 'right' },
      { key: 'discount', label: 'Discount', on: false, align: 'right' },
      { key: 'rate', label: 'Rate', on: true, align: 'right' },
      { key: 'tax', label: 'Tax', on: false, align: 'right' },
      { key: 'status', label: 'Status', on: true },
      { key: 'invoice', label: 'Invoice', on: true },
      { key: 'order', label: 'Order', on: false },
      { key: 'factura', label: 'Factura', on: false },
      { key: 'creative', label: 'Creative', on: false },
      { key: 'instructions', label: 'Instructions', on: false },
      { key: 'created_by', label: 'Booked by', on: false },
    ],
  },
  client_orders: {
    label: 'Orders',
    columns: [
      { key: 'created', label: 'Created', on: true },
      { key: 'items', label: 'Items', on: true },
      { key: 'packages', label: 'Packages', on: false },
      { key: 'rep', label: 'Rep', on: true },
      { key: 'subtotal', label: 'Subtotal', on: false, align: 'right' },
      { key: 'discount', label: 'Discount', on: true, align: 'right' },
      { key: 'total', label: 'Total', on: true, align: 'right' },
      { key: 'status', label: 'Status', on: true },
      { key: 'invoice', label: 'Invoice', on: true },
      { key: 'notes', label: 'Notes', on: false },
      { key: 'created_by', label: 'Created by', on: false },
    ],
  },
  client_invoices: {
    label: 'Invoices',
    columns: [
      { key: 'number', label: 'Invoice #', on: true, locked: true },
      { key: 'created', label: 'Created', on: true },
      { key: 'editions', label: 'Editions', on: false },
      { key: 'items', label: 'Items', on: false, align: 'right' },
      { key: 'subtotal', label: 'Subtotal', on: false, align: 'right' },
      { key: 'tax', label: 'Tax', on: false, align: 'right' },
      { key: 'total', label: 'Total', on: true, align: 'right' },
      { key: 'exchange_rate', label: 'Exchange rate', on: false, align: 'right', needs: 'multiCurrency' },
      { key: 'total_mxn', label: 'Total MXN', on: false, align: 'right', needs: 'multiCurrency' },
      { key: 'status', label: 'Status', on: true },
      { key: 'emailed', label: 'Emailed', on: false },
      { key: 'paid', label: 'Paid on', on: true },
      { key: 'paid_how', label: 'Paid how', on: false },
      { key: 'created_by', label: 'Created by', on: false },
      { key: 'pdf', label: 'PDF', on: true, locked: true, align: 'right' },
    ],
  },
  client_payments: {
    label: 'Payments',
    columns: [
      { key: 'date', label: 'Date', on: true },
      { key: 'recorded', label: 'Recorded', on: false },
      { key: 'reference', label: 'Reference', on: true },
      { key: 'method', label: 'Method', on: true },
      { key: 'invoices', label: 'For invoices', on: true },
      { key: 'amount', label: 'Amount', on: true, align: 'right' },
      { key: 'exchange_rate', label: 'Exchange rate', on: false, align: 'right', needs: 'multiCurrency' },
      { key: 'balance', label: 'Balance', on: true, align: 'right' },
      { key: 'notes', label: 'Notes', on: false },
      { key: 'recorded_by', label: 'Recorded by', on: false },
    ],
  },
};

// Columns this property can offer for a table.
export const availableColumns = (table, ctx = {}) =>
  ((TABLES[table] && TABLES[table].columns) || []).filter(c => c.needs !== 'multiCurrency' || ctx.multiCurrency);

export const defaultKeys = (table, ctx = {}) => availableColumns(table, ctx).filter(c => c.on || c.locked).map(c => c.key);

// The columns to draw, in table order. `saved` is the user's list of
// visible keys (or null/undefined for the default view). Locked columns are
// always included; unknown keys in `saved` are ignored; if nothing usable
// is left, the default view is used.
export const visibleColumns = (table, saved, ctx = {}) => {
  const cols = availableColumns(table, ctx);
  if (!Array.isArray(saved)) return cols.filter(c => c.on || c.locked);
  const want = new Set(saved);
  const out = cols.filter(c => c.locked || want.has(c.key));
  if (!out.some(c => !c.locked)) return cols.filter(c => c.on || c.locked);
  return out;
};

// The saved list after ticking or unticking one column. At least one
// non-locked column always stays visible.
export const toggleColumn = (table, saved, key, ctx = {}) => {
  const current = visibleColumns(table, saved, ctx).map(c => c.key);
  const col = availableColumns(table, ctx).find(c => c.key === key);
  if (!col || col.locked) return current;
  if (current.includes(key)) {
    const next = current.filter(k => k !== key);
    const lockedKeys = new Set(availableColumns(table, ctx).filter(c => c.locked).map(c => c.key));
    return next.some(k => !lockedKeys.has(k)) ? next : current;
  }
  const order = availableColumns(table, ctx).map(c => c.key);
  return order.filter(k => k === key || current.includes(k));
};

export const isDefaultView = (table, saved, ctx = {}) => {
  const a = visibleColumns(table, saved, ctx).map(c => c.key).join(',');
  return a === defaultKeys(table, ctx).join(',');
};
